/**
 * Uploaded Rosters, system admin only. Everything here lives under `/api/admin/roster-uploads`,
 * except a prospect's import history (`/api/admin/prospects/{id}/sources`), which is the other end of
 * the same provenance link.
 *
 * **Responses are normalised rather than cast**, for the same reason as `admin-billing.ts`: web and
 * the API deploy on separate workflows, so a new build can meet an older server for a few minutes.
 * Normalising at the boundary means a missing field becomes a dash in a cell rather than an
 * `undefined` reaching `.toLocaleString()` deep inside a table.
 */

import { get } from './api';
import type {
  GetRosterUploadsParams,
  ProspectSource,
  ProspectSourceRosterExtraction,
  ProspectSourcesResponse,
  RosterUploadDetail,
  RosterUploadFile,
  RosterUploadListItem,
  RosterUploadListResponse,
  RosterUploadOrganization,
  RosterUploadPassMetrics,
  RosterUploadProspect,
  RosterUploadSummary,
  RosterUploadUploader,
} from '../types/adminRosterUploads';

const BASE_URL = '/api/admin/roster-uploads';
const PROSPECTS_URL = '/api/admin/prospects';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const nullableStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const bool = (value: unknown): boolean => value === true;

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const nullableNum = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const array = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const stringList = (value: unknown): string[] =>
  array(value).filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);

const notNull = <T>(value: T | null): value is T => value !== null;

/**
 * The list's query string. Only filters that are set are sent, so the default view asks the API for
 * exactly what it defaults to and an empty search never reaches it as `search=`.
 */
export const buildRosterUploadsQuery = (params: GetRosterUploadsParams = {}): URLSearchParams => {
  const query = new URLSearchParams();
  if (params.organizationId) query.set('organizationId', params.organizationId);
  if (params.status) query.set('status', params.status);
  if (params.sourceType) query.set('sourceType', params.sourceType);
  const search = params.search?.trim();
  if (search) query.set('search', search);
  if (params.pageNumber && params.pageNumber > 1) query.set('pageNumber', String(params.pageNumber));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  return query;
};

const normalizeUploader = (raw: unknown): RosterUploadUploader | null => {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const email = str(raw.email);
  if (!id && !email) return null;
  return { id, name: nullableStr(raw.name), email };
};

const normalizeListItem = (raw: unknown): RosterUploadListItem | null => {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    organizationId: str(raw.organizationId),
    organizationName: str(raw.organizationName),
    uploadedBy: normalizeUploader(raw.uploadedBy),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
    sourceType: nullableStr(raw.sourceType),
    uploadType: nullableStr(raw.uploadType),
    status: nullableStr(raw.status),
    playerCount: nullableNum(raw.playerCount),
    fileCount: num(raw.fileCount),
    llmModel: nullableStr(raw.llmModel),
    totalTokens: nullableNum(raw.totalTokens),
    processingTimeMs: nullableNum(raw.processingTimeMs),
    validationFlagCount: num(raw.validationFlagCount),
    validationMadeCorrections: bool(raw.validationMadeCorrections),
    hasError: bool(raw.hasError),
    teamNames: stringList(raw.teamNames),
  };
};

const normalizeSummary = (raw: unknown): RosterUploadSummary => {
  const record = isRecord(raw) ? raw : {};
  return {
    totalUploads: num(record.totalUploads),
    uploadsLast7Days: num(record.uploadsLast7Days),
    uploadsLast30Days: num(record.uploadsLast30Days),
    completedCount: num(record.completedCount),
    failedCount: num(record.failedCount),
    inProgressCount: num(record.inProgressCount),
    distinctOrganizations: num(record.distinctOrganizations),
    distinctUploaders: num(record.distinctUploaders),
    avgProcessingTimeMs: nullableNum(record.avgProcessingTimeMs),
    totalTokens: num(record.totalTokens),
    lastUploadAt: nullableStr(record.lastUploadAt),
  };
};

const normalizeOrganization = (raw: unknown): RosterUploadOrganization | null => {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  if (!id) return null;
  return {
    id,
    name: str(raw.name),
    uploadCount: num(raw.uploadCount),
    lastUploadAt: nullableStr(raw.lastUploadAt),
  };
};

const normalizePassMetrics = (raw: unknown): RosterUploadPassMetrics => {
  const record = isRecord(raw) ? raw : {};
  return {
    pass1Tokens: num(record.pass1Tokens),
    pass1Ms: num(record.pass1Ms),
    pass2Tokens: num(record.pass2Tokens),
    pass2Ms: num(record.pass2Ms),
    pass3Tokens: num(record.pass3Tokens),
    pass3Ms: num(record.pass3Ms),
  };
};

const normalizeFile = (raw: unknown): RosterUploadFile | null => {
  if (!isRecord(raw)) return null;
  const url = str(raw.url);
  const fileName = str(raw.fileName);
  if (!url && !fileName) return null;
  return {
    url,
    signedUrl: nullableStr(raw.signedUrl),
    fileName: fileName || 'Unnamed file',
    isImage: bool(raw.isImage),
  };
};

/** A cell is a string or null; anything else the server sends is shown as its text. */
const normalizeCell = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : String(value);
};

const normalizeDetail = (raw: unknown): RosterUploadDetail | null => {
  const base = normalizeListItem(raw);
  if (!base || !isRecord(raw)) return null;
  return {
    ...base,
    errorMessage: nullableStr(raw.errorMessage),
    llmProvider: nullableStr(raw.llmProvider),
    eventId: nullableStr(raw.eventId),
    columns: array(raw.columns).map((column, index) =>
      typeof column === 'string' && column.length > 0 ? column : `Column ${index + 1}`
    ),
    players: array(raw.players).map((row) => array(row).map(normalizeCell)),
    playersTruncated: bool(raw.playersTruncated),
    validationFlags: stringList(raw.validationFlags),
    pass3MatchStrategy: nullableStr(raw.pass3MatchStrategy),
    pass3NumbersChanged: num(raw.pass3NumbersChanged),
    pass3MissingPlayerCount: num(raw.pass3MissingPlayerCount),
    passMetrics: normalizePassMetrics(raw.passMetrics),
    files: array(raw.files).map(normalizeFile).filter(notNull),
    signedUrlExpiresAt: nullableStr(raw.signedUrlExpiresAt),
  };
};

/** Latest uploads, newest first (the server's order), plus a summary over everything matched. */
export const getRosterUploads = async (
  params: GetRosterUploadsParams = {},
  signal?: AbortSignal
): Promise<RosterUploadListResponse> => {
  const qs = buildRosterUploadsQuery(params).toString();
  const raw = await get<unknown>(`${BASE_URL}${qs ? `?${qs}` : ''}`, { signal });
  const record = isRecord(raw) ? raw : {};
  const items = array(record.items).map(normalizeListItem).filter(notNull);
  const pageSize = num(record.pageSize, params.pageSize ?? Math.max(items.length, 1));
  const totalCount = num(record.totalCount, items.length);

  return {
    items,
    pageNumber: num(record.pageNumber, params.pageNumber ?? 1),
    pageSize,
    totalCount,
    totalPages: num(record.totalPages, pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0),
    summary: normalizeSummary(record.summary),
  };
};

/** Organizations with at least one upload, most recent first, for the organization filter. */
export const getRosterUploadOrganizations = async (
  signal?: AbortSignal
): Promise<RosterUploadOrganization[]> => {
  const raw = await get<unknown>(`${BASE_URL}/organizations`, { signal });
  return array(raw).map(normalizeOrganization).filter(notNull);
};

/**
 * One upload with its players, metrics and signed file URLs. A 404 rejects with the `ApiError`
 * from `api.ts`, so the caller can tell "gone" from "failed".
 */
export const getRosterUpload = async (
  id: string,
  signal?: AbortSignal
): Promise<RosterUploadDetail> => {
  const raw = await get<unknown>(`${BASE_URL}/${encodeURIComponent(id)}`, { signal });
  const detail = normalizeDetail(raw);
  if (!detail) {
    throw new Error('The roster upload response was not readable.');
  }
  return detail;
};

const normalizeUploadProspect = (raw: unknown): RosterUploadProspect | null => {
  if (!isRecord(raw)) return null;
  // The row id keys the list; a row without one (an older server) cannot be told apart from its
  // siblings for the same prospect, so it is dropped like any other unreadable row.
  const id = str(raw.id);
  const prospectId = str(raw.prospectId);
  if (!id || !prospectId) return null;
  return {
    id,
    prospectId,
    organizationId: str(raw.organizationId),
    firstName: str(raw.firstName),
    lastName: str(raw.lastName),
    gradYear: nullableNum(raw.gradYear),
    position: nullableStr(raw.position),
    teamId: nullableStr(raw.teamId),
    teamName: nullableStr(raw.teamName),
    outcome: str(raw.outcome),
    importedAt: str(raw.importedAt),
    importedBy: normalizeUploader(raw.importedBy),
    prospectDeleted: bool(raw.prospectDeleted),
  };
};

/**
 * The prospects a roster upload produced or matched, in the server's (last, first name) order.
 * A missing upload rejects with the 404 `ApiError`.
 */
export const getRosterUploadProspects = async (
  id: string,
  signal?: AbortSignal
): Promise<RosterUploadProspect[]> => {
  const raw = await get<unknown>(`${BASE_URL}/${encodeURIComponent(id)}/prospects`, { signal });
  return array(raw).map(normalizeUploadProspect).filter(notNull);
};

const normalizeSourceRosterExtraction = (raw: unknown): ProspectSourceRosterExtraction | null => {
  if (!isRecord(raw)) return null;
  return {
    createdAt: str(raw.createdAt),
    sourceType: nullableStr(raw.sourceType),
    fileCount: num(raw.fileCount),
  };
};

const normalizeProspectSource = (raw: unknown): ProspectSource | null => {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  if (!id) return null;
  const rosterExtractionId = nullableStr(raw.rosterExtractionId);
  return {
    id,
    sourceKind: str(raw.sourceKind),
    outcome: str(raw.outcome),
    importedAt: str(raw.importedAt),
    importedBy: normalizeUploader(raw.importedBy),
    teamId: nullableStr(raw.teamId),
    teamName: nullableStr(raw.teamName),
    rosterExtractionId,
    // Only meaningful beside the id it describes; a summary with nothing to link to is dropped.
    rosterExtraction: rosterExtractionId
      ? normalizeSourceRosterExtraction(raw.rosterExtraction)
      : null,
    batchExtractionId: nullableStr(raw.batchExtractionId),
    batchName: nullableStr(raw.batchName),
  };
};

/**
 * Every source a prospect came from (imports and manual entries), newest first (the server's
 * order), plus when and by whom the record was created. Cross-organization and system admin only —
 * callers must not ask for anyone else. A missing prospect rejects with 404.
 *
 * A bare array is still read as the source list, so a new build meeting a server from before the
 * response became an object shows the history rather than nothing.
 */
export const getProspectSources = async (
  prospectId: string,
  signal?: AbortSignal
): Promise<ProspectSourcesResponse> => {
  const raw = await get<unknown>(`${PROSPECTS_URL}/${encodeURIComponent(prospectId)}/sources`, {
    signal,
  });
  const record = isRecord(raw) ? raw : {};
  return {
    sources: array(Array.isArray(raw) ? raw : record.sources)
      .map(normalizeProspectSource)
      .filter(notNull),
    prospectCreatedAt: nullableStr(record.prospectCreatedAt),
    prospectCreatedBy: normalizeUploader(record.prospectCreatedBy),
  };
};
